/**
 * GBI Cart — Alpine.js store for AJAX cart management.
 *
 * Provides global cart state accessible via Alpine.store('cart').
 * Handles add, update, remove operations through Shopify AJAX endpoints.
 * Manages cart drawer open/close state.
 * Includes request queuing, error handling, and locale-aware currency formatting.
 */
document.addEventListener('alpine:init', function () {
  Alpine.store('cart', {
    items: [],
    itemCount: 0,
    totalPrice: 0,
    note: '',
    noteSaving: false,
    noteSaved: false,
    noteError: null,
    freeShippingThreshold: 0,
    drawerOpen: false,
    loading: false,
    error: null,
    _noteTimer: null,
    _noteSavedTimer: null,
    _noteErrorTimer: null,
    _queue: Promise.resolve(),
    _currencyCode: null,
    _currencyFormatter: null,
    _snapshot: null,
    _normalizing: false,

    _takeSnapshot() {
      this._snapshot = {
        items: JSON.parse(JSON.stringify(this.items)),
        itemCount: this.itemCount,
        totalPrice: this.totalPrice,
        note: this.note
      };
    },

    _restoreSnapshot() {
      if (!this._snapshot) return;
      this.items = this._snapshot.items;
      this.itemCount = this._snapshot.itemCount;
      this.totalPrice = this._snapshot.totalPrice;
      this.note = this._snapshot.note;
      this._snapshot = null;
    },

    announce(message) {
      var el = document.getElementById('sr-announcer');
      if (el) { el.textContent = ''; requestAnimationFrame(function () { el.textContent = message; }); }
    },

    announceAssertive(message) {
      var el = document.getElementById('sr-announcer-assertive');
      if (el) { el.textContent = ''; requestAnimationFrame(function () { el.textContent = message; }); }
    },

    _showError(message, duration) {
      this.error = message;
      this.announceAssertive(message);
      setTimeout(() => { this.error = null; }, duration || 5000);
    },

    _parseErrorMessage(responseData) {
      if (!responseData) return null;
      var desc = responseData.description;
      if (typeof desc === 'string') return desc;
      if (typeof responseData.message === 'string') return responseData.message;
      return null;
    },

    _friendlyMessage(rawMessage) {
      if (!rawMessage) return 'Something went wrong. Please try again.';
      var lower = rawMessage.toLowerCase();
      if (lower.includes('sold out') || lower.includes('not available') || lower.includes('not enough')) {
        return 'This item is sold out';
      }
      if (lower.includes('cart quantity') || lower.includes('cannot add')) {
        return rawMessage;
      }
      return rawMessage;
    },

    _fetchWithTimeout(url, options, timeout) {
      timeout = timeout || 8000;
      var controller = new AbortController();
      var id = setTimeout(function () { controller.abort(); }, timeout);
      options = options || {};
      options.signal = controller.signal;
      return fetch(url, options).finally(function () { clearTimeout(id); });
    },

    async _fetchWithRetry(url, options, timeout) {
      try {
        var res = await this._fetchWithTimeout(url, options, timeout);
        if (res.status >= 500) {
          await new Promise(function (r) { setTimeout(r, 1000); });
          return this._fetchWithTimeout(url, Object.assign({}, options), timeout);
        }
        return res;
      } catch (e) {
        if (e.name === 'AbortError' || e.name === 'TypeError') {
          await new Promise(function (r) { setTimeout(r, 1000); });
          return this._fetchWithTimeout(url, Object.assign({}, options), timeout);
        }
        throw e;
      }
    },

    _enqueue(fn) {
      this._queue = this._queue.catch(function () {}).then(fn);
      return this._queue;
    },

    async init() {
      if (this._noteTimer) clearTimeout(this._noteTimer);
      if (this._noteSavedTimer) clearTimeout(this._noteSavedTimer);
      if (this._noteErrorTimer) clearTimeout(this._noteErrorTimer);
      var thresholdEl = document.querySelector('[data-free-shipping-threshold]');
      if (thresholdEl) {
        this.freeShippingThreshold = parseInt(thresholdEl.dataset.freeShippingThreshold, 10) || 0;
      }
      this._currencyCode = document.body.dataset.shopCurrency || 'USD';
      try {
        this._currencyFormatter = new Intl.NumberFormat(undefined, {
          style: 'currency',
          currency: this._currencyCode
        });
      } catch (e) {
        this._currencyFormatter = null;
      }
      await this.refresh();
    },

    // Boxes and subscriptions ship from their own profile and do NOT count
    // toward the free-shipping minimum (Ben, 2026-07-29). Box products are the
    // only ones whose handle starts with "reseller-"; keep that naming
    // convention when adding new box products or the bar will overcount.
    _freeShippingEligibleTotal() {
      var total = 0;
      var items = this.items || [];
      for (var i = 0; i < items.length; i++) {
        var it = items[i];
        if (it.handle && it.handle.indexOf('reseller-') === 0) continue;
        total += (typeof it.final_line_price === 'number' ? it.final_line_price : it.line_price) || 0;
      }
      return total;
    },

    get freeShippingRemaining() {
      if (this.freeShippingThreshold <= 0) return 0;
      var thresholdCents = this.freeShippingThreshold * 100;
      return Math.max(0, thresholdCents - this._freeShippingEligibleTotal());
    },

    get freeShippingPercent() {
      if (this.freeShippingThreshold <= 0) return 0;
      var thresholdCents = this.freeShippingThreshold * 100;
      return Math.min(100, (this._freeShippingEligibleTotal() / thresholdCents) * 100);
    },

    async updateNote(text) {
      this.note = text;
      this.noteError = null;
      if (this._noteTimer) clearTimeout(this._noteTimer);
      if (this._noteSavedTimer) clearTimeout(this._noteSavedTimer);
      if (this._noteErrorTimer) clearTimeout(this._noteErrorTimer);
      this._noteTimer = setTimeout(async () => {
        this.noteSaving = true;
        this.noteSaved = false;
        try {
          var res = await this._fetchWithTimeout('/cart/update.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ note: this.note })
          });
          if (!res.ok) {
            this.noteSaving = false;
            this.noteError = 'Could not save note';
            this._noteErrorTimer = setTimeout(() => { this.noteError = null; }, 5000);
            return;
          }
          this.noteSaving = false;
          this.noteSaved = true;
          this._noteSavedTimer = setTimeout(() => { this.noteSaved = false; }, 1500);
        } catch (e) {
          this.noteSaving = false;
          this.noteError = 'Could not save note';
          this._noteErrorTimer = setTimeout(() => { this.noteError = null; }, 5000);
        }
      }, 500);
    },

    async refresh() {
      try {
        var res = await this._fetchWithRetry('/cart.js');
        if (!res.ok) {
          throw new Error('Failed to load cart');
        }
        var cart;
        try { cart = await res.json(); } catch (parseErr) {
          this._showError('Could not load cart. Please refresh the page.');
          return;
        }
        this.items = cart.items;
        this.itemCount = cart.item_count;
        this.totalPrice = cart.total_price;
        this.note = cart.note || '';
        // Auto-snap any line item whose qty drifted out of pack/min constraints.
        // Catches stale carts, direct API edits, and any path that bypassed
        // per-control validation. Guarded by _normalizing so the re-fetch
        // inside the normalizer doesn't recurse.
        if (!this._normalizing) {
          await this._normalizeCartIfNeeded();
        }
      } catch (e) {
        if (e.name === 'AbortError') {
          this._showError('Request timed out. Please try again.');
        } else if (e.name === 'TypeError') {
          this._showError('Check your connection and try again.');
        } else {
          this._showError('Something went wrong. Please try again.');
        }
      }
    },

    // Snap a quantity to the nearest valid pack/min based on the case_size
    // and min_qty values carried in line item properties.
    _snapToPack(quantity, caseSize, minQty) {
      var q = parseInt(quantity, 10) || 0;
      var step = parseInt(caseSize, 10) || 0;
      var min = parseInt(minQty, 10) || 0;
      if (step > 1) {
        q = Math.round(q / step) * step;
        if (q < step) q = step;
      }
      if (min > 1 && q < min) {
        if (step > 1) {
          q = Math.ceil(min / step) * step;
        } else {
          q = min;
        }
      }
      return Math.max(1, q);
    },

    // Walk the cart after a /cart.js refresh and snap any non-conforming
    // line items via /cart/change.js. Sets _normalizing so the post-fix
    // refresh doesn't recursively call this method.
    async _normalizeCartIfNeeded() {
      if (!this.items || !this.items.length) return;
      var fixes = [];
      for (var i = 0; i < this.items.length; i++) {
        var item = this.items[i];
        if (!this.needsSnap(item)) continue;
        var step = this.getItemStep(item);
        var min = this.getItemMin(item);
        var snapped = item.quantity;
        if (step > 1) snapped = Math.round(snapped / step) * step;
        if (snapped < min) snapped = min;
        if (snapped !== item.quantity) fixes.push({ key: item.key, quantity: snapped });
      }
      if (!fixes.length) return;
      this._normalizing = true;
      try {
        await this._applyLineChanges(fixes);
        // Inventory-capped lines: Shopify trims a line to what is in stock, so the
        // snap above can come back unchanged. Below one case that is a sellable
        // remainder (Ben, 2026-09-15): re-stamp the line's step to its quantity so
        // needsSnap/the checkout guard stop flagging it. At or above one case,
        // floor to the last full multiple (the leftover stays on the shelf).
        var heals = [];
        for (var h = 0; h < this.items.length; h++) {
          var it = this.items[h];
          if (!this.needsSnap(it)) continue;
          var st = this.getItemStep(it);
          if (st <= 1) continue;
          if (it.quantity < st) {
            var props = Object.assign({}, it.properties || {}, {
              _case_size: String(it.quantity),
              _min_qty: String(Math.min(this.getItemMin(it), it.quantity))
            });
            heals.push({ key: it.key, quantity: it.quantity, properties: props });
          } else {
            heals.push({ key: it.key, quantity: Math.floor(it.quantity / st) * st });
          }
        }
        if (heals.length) await this._applyLineChanges(heals);
        if (window.console && console.info) {
          console.info('[gbi-cart] Auto-snapped ' + fixes.length + ' line item(s) to pack quantity' + (heals.length ? ', healed ' + heals.length + ' inventory-capped' : ''));
        }
      } finally {
        this._normalizing = false;
      }
    },

    // POST each change to /cart/change.js, then re-fetch /cart.js into state
    // (without re-triggering normalize).
    async _applyLineChanges(changes) {
      for (var j = 0; j < changes.length; j++) {
        try {
          var body = { id: changes[j].key, quantity: changes[j].quantity };
          if (changes[j].properties) body.properties = changes[j].properties;
          await this._fetchWithRetry('/cart/change.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(body)
          });
        } catch (e) {
          if (window.console && console.warn) console.warn('[gbi-cart] normalize failed for', changes[j].key, e);
        }
      }
      try {
        var res = await this._fetchWithRetry('/cart.js');
        if (res.ok) {
          var cart = await res.json();
          this.items = cart.items;
          this.itemCount = cart.item_count;
          this.totalPrice = cart.total_price;
          this.note = cart.note || '';
        }
      } catch (e) { /* swallow: changes already posted */ }
    },

    async add(variantId, quantity, properties) {
      quantity = quantity || 1;
      // Backstop: snap entry qty using properties' case_size/min_qty so internal
      // callers passing a raw qty (DevTools, future callers) cannot bypass.
      // Authoritative enforcement (catches no-properties paths) happens in
      // refresh() -> _normalizeCartIfNeeded() after the POST settles.
      if (properties && typeof properties === 'object') {
        quantity = this._snapToPack(quantity, properties._case_size, properties._min_qty);
      }
      return this._enqueue(async () => {
        this.loading = true;
        this._takeSnapshot();
        try {
          var payload = { id: variantId, quantity: quantity };
          if (properties && typeof properties === 'object' && Object.keys(properties).length) {
            payload.properties = properties;
          }
          var res = await this._fetchWithRetry('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload)
          });
          var body;
          try { body = await res.json(); } catch (parseErr) {
            this._restoreSnapshot();
            this._showError('Could not process cart response. Please try again.');
            this.loading = false;
            return;
          }
          if (!res.ok) {
            this._restoreSnapshot();
            var raw = this._parseErrorMessage(body);
            this._showError(this._friendlyMessage(raw));
            this.loading = false;
            return;
          }
          this._snapshot = null;
          var title = body.product_title || body.title || 'Item';
          await this.refresh();
          this.announce('Added ' + title + ' to cart. ' + this.itemCount + ' ' + (this.itemCount === 1 ? 'item' : 'items') + ' in cart.');
          this.drawerOpen = true;
        } catch (e) {
          this._restoreSnapshot();
          if (e.name === 'AbortError') {
            this._showError('Request timed out. Please try again.');
          } else if (e.name === 'TypeError') {
            this._showError('Check your connection and try again.');
          } else {
            this._showError('Something went wrong. Please try again.');
          }
        }
        this.loading = false;
      });
    },

    async update(key, quantity) {
      return this._enqueue(async () => {
        this.loading = true;
        this._takeSnapshot();
        var removing = quantity === 0;

        // Snap to valid case-pack multiple. If items list is empty (first
        // interaction before refresh has populated it), hydrate once before
        // looking up the item — otherwise the snap is silently skipped and
        // an unconforming qty hits /cart/change.js.
        if (!removing) {
          var item = this.items.find(function (i) { return i.key === key; });
          if (!item && (!this.items || !this.items.length)) {
            await this.refresh();
            item = this.items.find(function (i) { return i.key === key; });
          }
          if (item) {
            var step = this.getItemStep(item);
            var min = this.getItemMin(item);
            if (step > 1) {
              quantity = Math.round(quantity / step) * step;
              if (quantity < min) quantity = min;
            } else if (min > 1 && quantity < min) {
              quantity = min;
            }
          }
        }

        try {
          var res = await this._fetchWithRetry('/cart/change.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ id: key, quantity: quantity })
          });
          var body;
          try { body = await res.json(); } catch (parseErr) {
            this._restoreSnapshot();
            this._showError('Could not process cart response. Please try again.');
            this.loading = false;
            return;
          }
          if (!res.ok) {
            this._restoreSnapshot();
            var raw = this._parseErrorMessage(body);
            this._showError(this._friendlyMessage(raw));
            this.loading = false;
            return;
          }
          this._snapshot = null;
          await this.refresh();
          if (removing) {
            this.announce('Item removed. ' + this.itemCount + ' ' + (this.itemCount === 1 ? 'item' : 'items') + ' in cart.');
          } else {
            this.announce('Cart updated. ' + this.itemCount + ' ' + (this.itemCount === 1 ? 'item' : 'items') + ' in cart.');
          }
        } catch (e) {
          this._restoreSnapshot();
          if (e.name === 'AbortError') {
            this._showError('Request timed out. Please try again.');
          } else if (e.name === 'TypeError') {
            this._showError('Check your connection and try again.');
          } else {
            this._showError('Something went wrong. Please try again.');
          }
        }
        this.loading = false;
      });
    },

    async remove(key) {
      await this.update(key, 0);
    },

    // Pre-checkout guard: snap any non-conforming line item before navigating to /checkout.
    // Defends against direct API edits or stale client state that bypassed the per-control
    // validation (e.g., DevTools POST to /cart/change.js with qty=5 on a case_size=6 item).
    async goToCheckout(event) {
      if (event && typeof event.preventDefault === 'function') event.preventDefault();
      var target = (event && event.currentTarget && event.currentTarget.getAttribute('href')) || '/checkout';
      // The guard must never turn the checkout button into a dead click. Only a
      // CONFIRMED case-pack violation may block; infra failures (fetch rejected
      // or hung by third-party interceptors; Route and Unveild both wrap cart
      // requests on this store) fail OPEN and navigate. Reproduced 2026-07-29:
      // early drawer clicks did nothing because the guard never settled and the
      // old catch swallowed the click.
      var verdict;
      try {
        verdict = await Promise.race([
          this._checkoutGuard(),
          new Promise(function (resolve) { setTimeout(function () { resolve('timeout'); }, 2500); })
        ]);
      } catch (e) {
        if (window.console && console.warn) console.warn('Pre-checkout guard failed, proceeding to checkout', e);
        verdict = 'go';
      }
      if (verdict === 'block') return;
      if (verdict === 'timeout' && window.console && console.warn) {
        console.warn('Pre-checkout guard timed out, proceeding to checkout');
      }
      window.location.href = target;
    },

    // Returns 'block' when a confirmed case-pack violation remains, 'go' otherwise.
    // Throws on infra errors; goToCheckout treats those as fail-open.
    async _checkoutGuard() {
      if (!this.items || !this.items.length) {
        await this.refresh();
      }
      var fixes = [];
      for (var i = 0; i < this.items.length; i++) {
        var item = this.items[i];
        var step = this.getItemStep(item);
        var min = this.getItemMin(item);
        var current = item.quantity;
        var snapped = current;
        if (step > 1) {
          snapped = Math.round(current / step) * step;
        }
        if (snapped < min) snapped = min;
        if (snapped !== current) {
          fixes.push({ key: item.key, quantity: snapped });
        }
      }
      if (fixes.length) {
        for (var j = 0; j < fixes.length; j++) {
          await this.update(fixes[j].key, fixes[j].quantity);
        }
        this.announce('Adjusted ' + fixes.length + ' item' + (fixes.length === 1 ? '' : 's') + ' to pack quantity before checkout.');
      }
      // Verify-and-block: re-fetch and confirm every line item now conforms.
      // Snap-up can silently fail when inventory is insufficient (e.g. qty=4,
      // case_pack=6, inventory=4 → /cart/change.js returns 422 and the cart
      // is left in a violating state). Without this guard, the customer would
      // proceed to checkout with the bad qty (see order #GBI1204, 2026-05-17).
      await this.refresh();
      var blockers = [];
      for (var k = 0; k < this.items.length; k++) {
        if (this.needsSnap(this.items[k])) blockers.push(this.items[k]);
      }
      if (blockers.length) {
        var first = blockers[0];
        var step = this.getItemStep(first);
        var msg = blockers.length === 1
          ? first.product_title + ' must be ordered in packs of ' + step + '. Please adjust the quantity or remove the item.'
          : blockers.length + ' items in your cart must be ordered in packs. Please adjust quantities before checkout.';
        this._showError(msg, 10000);
        this.drawerOpen = true;
        return 'block';
      }
      return 'go';
    },

    _productConstraint(item) {
      // Server-emitted fallback from snippets/cart-drawer.liquid. Used when
      // a line item is missing _case_size / _min_qty properties (e.g., it
      // was added before properties were attached). Keyed by product_id.
      var map = (typeof window !== 'undefined' && window.gbiProductConstraints) || null;
      if (!map || !item || item.product_id == null) return null;
      return map[item.product_id] || map[String(item.product_id)] || null;
    },

    getItemStep(item) {
      if (item.properties && item.properties._case_size) {
        var s = parseInt(item.properties._case_size, 10);
        if (s > 0) return s;
      }
      // _min_qty attached without _case_size is the theme's explicit signal
      // that the line item has no case-pack step (resellers, mystery boxes,
      // subscription products). Use it as step and DO NOT fall back to the
      // product metafield — the metafield can disagree (e.g. mystery-box
      // products carrying case_pack_size=6 but sold individually) and falling
      // back would silently snap valid quantities up/down on every refresh.
      if (item.properties && item.properties._min_qty) {
        var m = parseInt(item.properties._min_qty, 10);
        if (m > 0) return m;
      }
      // Pure fallback for line items added before properties existed.
      var c = this._productConstraint(item);
      if (c && c.case_pack_size > 1) return c.case_pack_size;
      if (c && c.minimum_quantity > 1) return c.minimum_quantity;
      return 1;
    },

    needsSnap(item) {
      if (!item || item.quantity == null) return false;
      var step = this.getItemStep(item);
      var min = this.getItemMin(item);
      if (item.quantity < min) return true;
      if (step > 1 && item.quantity % step !== 0) return true;
      return false;
    },

    getItemMin(item) {
      if (item.properties && item.properties._min_qty) {
        var m = parseInt(item.properties._min_qty, 10);
        if (m > 0) return m;
      }
      var c = this._productConstraint(item);
      if (c && c.minimum_quantity > 0) return c.minimum_quantity;
      return Math.max(1, this.getItemStep(item));
    },

    getQtyOptions(item) {
      var step = this.getItemStep(item);
      var min = this.getItemMin(item);
      var max = window.gbiMaxLineQty || 96;
      // For case-pack products, ensure min aligns to a multiple of step
      if (step > 1 && min % step !== 0) {
        min = Math.ceil(min / step) * step;
      }
      // Partial-case remainder: step below the product's case pack means the line
      // holds what is left in stock, so it is the only valid quantity.
      var c = this._productConstraint(item);
      if (step > 1 && c && c.case_pack_size > step) return [step];
      var opts = [];
      for (var v = min; v <= max; v += step) opts.push(v);
      return opts.length ? opts : [this.getItemStep(item)];
    },

    formatMoney: function (cents) {
      if (this._currencyFormatter) {
        return this._currencyFormatter.format(cents / 100);
      }
      return (this._currencyCode || 'USD') + ' ' + (cents / 100).toFixed(2);
    }
  });

  // Checkout watchdog. Route's package-protection widget hides the theme's
  // checkout anchor (0x0) and swaps in its own button, so navigation depends
  // entirely on Route's script; when that errors or hangs (it races Unveild's
  // fetch interception on this store) the customer's click dies silently and
  // the drawer just closes. Capture-phase listener: any checkout-intent click
  // in the drawer footer arms a one-shot timer that force-navigates unless the
  // page is already unloading or the cart store deliberately blocked checkout
  // (case-pack violation sets store.error). Worst case the customer reaches
  // checkout without protection pre-attached; Shopify checkout re-offers it.
  var gbiCheckoutWatchdogArmed = false;
  document.addEventListener('click', function (e) {
    if (gbiCheckoutWatchdogArmed) return;
    if (!e.target || !e.target.closest) return;
    if (!e.target.closest('.glass-cart-totals')) return;
    var el = e.target.closest('a,button');
    if (!el) return;
    if (el.getAttribute && el.getAttribute('href') === '/cart') return;
    var label = (el.textContent || '').toLowerCase();
    if (label.indexOf('checkout') === -1 && label.indexOf('continue without') === -1) return;
    gbiCheckoutWatchdogArmed = true;
    setTimeout(function () {
      gbiCheckoutWatchdogArmed = false;
      var store = window.Alpine && Alpine.store && Alpine.store('cart');
      if (store && store.error) return;
      if (document.visibilityState === 'hidden') return;
      if (window.console && console.warn) console.warn('[gbi-cart] Checkout click produced no navigation after 3s, forcing /checkout');
      window.location.href = '/checkout';
    }, 3000);
  }, true);
});
